import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, X, Lock, Image, Video, Zap, Coins, ChevronDown, ArrowRight } from 'lucide-react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';
import { useScrollReveal } from '../components/Landing/useScrollReveal';
import { useAuthStore } from '../stores/authStore';
import { BillingService } from '../services/BillingService';
import type { BillingPlanDto } from '../services/BillingService';

const IS_ALPHA = import.meta.env.VITE_IS_ALPHA === 'true';

/* ═══════════════════════════════════════════════════════
 *  Constants
 * ═══════════════════════════════════════════════════════ */

const FALLBACK_FEATURES: { text: string; tooltipHtml?: string | null; isIncluded: boolean }[] = [
  { text: '4,000 credits every month', isIncluded: true },
  { text: 'All current image models', isIncluded: true },
  { text: 'All current video models', isIncluded: true },
  { text: 'Unlimited AI text generation', isIncluded: true },
  { text: 'Unlimited canvases & projects', isIncluded: true },
  { text: 'Image & video downloads', isIncluded: true },
  { text: 'Credit top-ups (Coming soon)', isIncluded: true },
  { text: 'Team collaboration (5 seats)', isIncluded: true },
];

interface ModelRow { name: string; type: 'image' | 'video'; baseCost: number; note?: string }

const MODEL_DATA: ModelRow[] = [
  { name: 'FLUX Schnell',             type: 'image', baseCost: 0.003 },
  { name: 'GPT Image 1.5 Low',        type: 'image', baseCost: 0.009 },
  { name: 'MiniMax Image',            type: 'image', baseCost: 0.01 },
  { name: 'Luma Photon',              type: 'image', baseCost: 0.019 },
  { name: 'FLUX Dev',                 type: 'image', baseCost: 0.025 },
  { name: 'Seedream V4',              type: 'image', baseCost: 0.03 },
  { name: 'Ideogram V3 Turbo',        type: 'image', baseCost: 0.03 },
  { name: 'Qwen Image Edit Plus',     type: 'image', baseCost: 0.03 },
  { name: 'GPT Image 1.5 Medium',     type: 'image', baseCost: 0.034 },
  { name: 'Seedream 5 Lite Edit',     type: 'image', baseCost: 0.035 },
  { name: 'Gemini 2.5 Flash',         type: 'image', baseCost: 0.039 },
  { name: 'Recraft V3',               type: 'image', baseCost: 0.04 },
  { name: 'FLUX Kontext Pro',         type: 'image', baseCost: 0.04 },
  { name: 'Seedream 4.5',             type: 'image', baseCost: 0.04 },
  { name: 'FLUX 2 Flex',              type: 'image', baseCost: 0.06 },
  { name: 'FLUX 1.1 Pro Ultra',       type: 'image', baseCost: 0.06 },
  { name: 'Ideogram V3',              type: 'image', baseCost: 0.06 },
  { name: 'Gemini 3.1 Flash',         type: 'image', baseCost: 0.067, note: '1K res' },
  { name: 'FLUX Kontext Max',         type: 'image', baseCost: 0.08 },
  { name: 'Ideogram V3 Quality',      type: 'image', baseCost: 0.09 },
  { name: 'GPT Image 1.5 High',       type: 'image', baseCost: 0.133 },
  { name: 'Gemini 3 Pro',             type: 'image', baseCost: 0.134, note: '1K res' },
  { name: 'Ideogram V3 Character',    type: 'image', baseCost: 0.20 },
  { name: 'Seedance 1.0 Fast',        type: 'video', baseCost: 0.25,  note: '5s clip' },
  { name: 'LTX Video 2.0',            type: 'video', baseCost: 0.25,  note: '5s clip' },
  { name: 'Hailuo-02',                type: 'video', baseCost: 0.27,  note: '6s clip' },
  { name: 'Kling 2.5 Turbo',          type: 'video', baseCost: 0.35,  note: '5s clip' },
  { name: 'Sora 2',                   type: 'video', baseCost: 0.40,  note: '4s clip' },
  { name: 'Hailuo-02 Pro',            type: 'video', baseCost: 0.48,  note: '6s clip' },
  { name: 'Seedance 1.5',             type: 'video', baseCost: 0.50,  note: '5s clip' },
  { name: 'Veo 3.1 Fast',             type: 'video', baseCost: 0.60,  note: '4s clip' },
  { name: 'Kling 3.0 Standard',       type: 'video', baseCost: 0.84,  note: '5s clip' },
  { name: 'Kling O3 Standard',        type: 'video', baseCost: 0.84,  note: '5s clip' },
  { name: 'Kling 3.0 Pro',            type: 'video', baseCost: 1.12,  note: '5s clip' },
  { name: 'Kling O3 Pro',             type: 'video', baseCost: 1.12,  note: '5s clip' },
  { name: 'Sora 2 Pro',               type: 'video', baseCost: 1.20,  note: '4s clip' },
  { name: 'Veo 3.1',                  type: 'video', baseCost: 1.60,  note: '4s clip' },
];

const FAQ_ITEMS = [
  {
    q: 'How does pricing work?',
    a: 'Both plans give you 4,000 credits per month. Each AI generation costs a certain number of credits depending on the model \u2014 from 1 credit for fast models to ~500 for premium video. The credit cost is always shown before you generate.',
  },
  {
    q: 'What are credits?',
    a: 'Credits are Cascady\u2019s simple unit for AI generation. Cheap image models cost as little as 1 credit, premium video models up to ~500. Both plans include 4,000 credits/month \u2014 enough for hundreds of images or dozens of videos.',
  },
  {
    q: 'Can I buy more credits if I run out?',
    a: 'Credit top-ups are coming soon. For now, your 4,000 monthly credits refresh at the start of each billing cycle.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, cancel anytime with one click. No lock-in, no cancellation fees. Your remaining credits stay active until the end of your billing period.',
  },
  {
    q: 'What AI models are included?',
    a: 'All of them \u2014 30+ image models (Gemini, Seedream, FLUX, Recraft, Ideogram, GPT Image, and more) and 20+ video models (Veo, Sora, Kling, Hailuo, Seedance, and more). Every plan gets access to every model.',
  },
  {
    q: 'What\u2019s the difference between Monthly and Yearly?',
    a: 'Both plans include the same 4,000 credits/month and access to all models. The yearly plan saves you $38 compared to paying monthly \u2014 that\u2019s 2 months free.',
  },
];

/* ═══════════════════════════════════════════════════════
 *  Helpers
 * ═══════════════════════════════════════════════════════ */

const USD_PER_CREDIT = 0.003;

function fmtPrice(cents: number): string {
  const d = cents / 100;
  return d % 1 === 0 ? `$${d}` : `$${d.toFixed(2)}`;
}
function creditCost(baseCost: number): number {
  return Math.ceil(baseCost / USD_PER_CREDIT);
}
function maxFromBudget(baseCost: number, budgetCredits = 4000): number {
  return Math.floor(budgetCredits / creditCost(baseCost));
}

const getFeatures = (plan: BillingPlanDto | undefined) =>
  plan?.features?.length
    ? [...plan.features].sort((a, b) => a.displayOrder - b.displayOrder)
    : FALLBACK_FEATURES.map((f, i) => ({ ...f, id: String(i), displayOrder: i, category: null }));

/* ═══════════════════════════════════════════════════════
 *  FeatureRow — with tooltip (light + dark variants)
 * ═══════════════════════════════════════════════════════ */

function FeatureRow({
  text, tooltipHtml, included, dark, muted,
}: { text: string; tooltipHtml?: string | null; included: boolean; dark?: boolean; muted?: boolean }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<'above' | 'below'>('above');
  const rowRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const hasTip = !!tooltipHtml;

  const enter = () => {
    if (!hasTip) return;
    if (rowRef.current) setPos(rowRef.current.getBoundingClientRect().top > 300 ? 'above' : 'below');
    timer.current = window.setTimeout(() => setOpen(true), 180);
  };
  const leave = () => { if (timer.current) clearTimeout(timer.current); setOpen(false); };
  const toggle = () => {
    if (!hasTip) return;
    if (!open && rowRef.current) setPos(rowRef.current.getBoundingClientRect().top > 300 ? 'above' : 'below');
    setOpen(prev => !prev);
  };

  // Close tooltip on outside tap (mobile)
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (rowRef.current && !rowRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  let checkColor: string, textColor: string, underlineColor: string;
  if (!included) {
    checkColor = dark ? 'text-[#3e4452]' : 'text-[#d4d8e0]';
    textColor = dark ? 'text-[#3e4452]' : 'text-[#bec3cd]';
    underlineColor = 'transparent';
  } else if (dark) {
    checkColor = 'text-[#c8d45a]';
    textColor = muted ? 'text-[#7a8294]' : 'text-[#c9cdd6]';
    underlineColor = 'rgba(201,205,214,0.25)';
  } else {
    checkColor = muted ? 'text-[#c0c7d0]' : 'text-[#8b9656]';
    textColor = muted ? 'text-[#6b7280]' : 'text-[#3a4252]';
    underlineColor = 'rgba(0,0,0,0.12)';
  }

  return (
    <div
      ref={rowRef}
      className={`flex items-center gap-2.5 py-[5px] text-[0.8rem] leading-snug relative ${hasTip ? 'cursor-help select-none' : ''}`}
      onMouseEnter={enter} onMouseLeave={leave} onClick={toggle}
    >
      {included ? (
        <Check className={`w-[14px] h-[14px] shrink-0 ${checkColor}`} />
      ) : (
        <X className={`w-[14px] h-[14px] shrink-0 ${checkColor}`} />
      )}
      <span
        className={`${textColor} ${!included ? 'line-through' : ''}`}
        style={hasTip && included ? { borderBottom: `1px dotted ${underlineColor}`, paddingBottom: 1 } : undefined}
      >
        {text}
      </span>

      {hasTip && open && (
        <div
          className={`absolute left-0 z-50 w-[270px] px-3.5 py-3 rounded-[10px] text-[0.72rem] leading-relaxed pointer-events-none ${pos === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'}`}
          style={{ background: dark ? '#2a3040' : '#1a2233', color: 'rgba(255,255,255,0.78)', animation: 'prcTipIn 0.12s ease-out', boxShadow: '0 12px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06)' }}
        >
          <div dangerouslySetInnerHTML={{ __html: tooltipHtml! }} />
          <div className={`absolute left-5 w-[7px] h-[7px] rotate-45 ${pos === 'above' ? 'bottom-[-4px]' : 'top-[-4px]'}`} style={{ background: dark ? '#2a3040' : '#1a2233' }} />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
 *  ModelTable — light-mode table card
 * ═══════════════════════════════════════════════════════ */

function ModelTable({ title, subtitle, icon, models }: {
  title: string; subtitle: string; icon: React.ReactNode; models: ModelRow[];
}) {
  return (
    <div className="pricing-model-card bg-white rounded-2xl p-5 md:p-6" style={{ border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h3 className="text-[0.95rem] font-semibold text-[#1d2939]">{title}</h3>
      </div>
      <p className="text-[0.7rem] text-[#9ca3af] mb-4 ml-7">{subtitle}</p>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[0.8rem]">
          <thead>
            <tr className="border-b border-[#e5e7eb]">
              <th className="text-left py-2 px-2 text-[#9ca3af] font-medium text-[0.68rem] uppercase tracking-wider">Model</th>
              <th className="text-right py-2 px-2 text-[#9ca3af] font-medium text-[0.68rem] uppercase tracking-wider">Credits</th>
              <th className="text-right py-2 px-2 text-[#8b9656] font-medium text-[0.68rem] uppercase tracking-wider">Per month</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m, i) => (
              <tr key={i} className="border-b border-[#f0f1f3] pricing-model-row transition-colors">
                <td className="py-[6px] px-2">
                  <span className="text-[#3a4252] font-medium">{m.name}</span>
                  {m.note && <span className="text-[#9ca3af] text-[0.68rem] ml-1.5">{m.note}</span>}
                </td>
                <td className="py-[6px] px-2 text-right text-[#6b7280] tabular-nums">{creditCost(m.baseCost).toLocaleString()}</td>
                <td className="py-[6px] px-2 text-right text-[#8b9656] font-semibold tabular-nums">{maxFromBudget(m.baseCost).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[0.7rem] text-[#9ca3af] mt-3">
        Credit cost is always displayed before you generate.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
 *  Pricing Page
 * ═══════════════════════════════════════════════════════ */

export function Pricing() {
  const user = useAuthStore((s) => s.user);
  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const heroRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useScrollReveal<HTMLDivElement>();
  const calcRef = useScrollReveal<HTMLDivElement>();
  const creditsRef = useScrollReveal<HTMLDivElement>();
  const faqRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    BillingService.listPublicPlans()
      .then((data) => {
        setPlans(
          data.filter((p) => p.isActive && p.key !== 'alpha')
            .sort((a, b) => a.displayOrder - b.displayOrder),
        );
      })
      .catch((err) => console.error('Failed to load plans:', err))
      .finally(() => setLoading(false));
  }, []);

  const monthly = plans.find((p) => p.key === 'monthly' || p.key === 'plan_monthly');
  const yearly  = plans.find((p) => p.key === 'yearly'  || p.key === 'plan_yearly');
  const mPrice  = monthly?.prices.find((p) => p.interval === 'month');
  const yPrice  = yearly?.prices.find((p) => p.interval === 'year');
  const yMonthly = yPrice ? Math.round((yPrice.unitAmount / 12) * 100) / 100 : null;
  const savings  = mPrice && yPrice ? (mPrice.unitAmount * 12 - yPrice.unitAmount) / 100 : null;
  const ctaPath  = user ? '/organization/credits' : '/auth';

  // Payment Link URLs from the plans API (stored in billing_prices.payment_link_url)
  const monthlyPaymentLink = mPrice?.paymentLinkUrl || '';
  const yearlyPaymentLink = yPrice?.paymentLinkUrl || '';
  const monthlyHref = user ? '/organization/credits' : monthlyPaymentLink;
  const yearlyHref = user ? '/organization/credits' : yearlyPaymentLink;

  const imageModels = MODEL_DATA.filter(m => m.type === 'image');
  const videoModels = MODEL_DATA.filter(m => m.type === 'video');

  return (
    <div className="min-h-screen bg-[#fafbfd]">
      <Helmet>
        <title>Pricing — Cascady | AI Image & Video Generation Canvas</title>
        <meta name="description" content="One plan, every model. Access 60+ AI image and video models with a single Cascady subscription. See pricing, credits, and what's included." />
        <meta property="og:title" content="Simple pricing. Every model included." />
        <meta property="og:description" content="One plan, 60+ AI models. No per-model subscriptions, no lock-in. See what's included." />
        <meta property="og:url" content="https://cascady.ai/pricing" />
        <meta name="twitter:title" content="Simple pricing. Every model included." />
        <meta name="twitter:description" content="One plan, 60+ AI models. No per-model subscriptions, no lock-in. See what's included." />
      </Helmet>
      <PublicHeader />

      {/* ── SECTION 1: Hero ── */}
      <section
        className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden"
        style={{ background: '#fafbfd' }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(62,69,87,0.13) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 80%)',
          }}
        />
        {/* Subtle color wash */}
        <div
          className="absolute top-[-20%] left-[10%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(200,212,90,0.06) 0%, transparent 70%)' }}
        />

        <Container size="lg" className="max-w-[1060px] relative z-[1]">
          <div ref={heroRef} className="lp-reveal text-center">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#a8b44a] mb-3 font-semibold">
              Pricing
            </p>
            <h1
              className="text-[clamp(2rem,5vw,3.2rem)] font-bold leading-[1.1] tracking-[-0.02em] mb-4"
              style={{ color: '#111827' }}
            >
              Simple, <em
                style={{
                  fontFamily: "'Playfair Display',Georgia,serif",
                  fontWeight: 400,
                  fontStyle: 'italic',
                  color: '#7b6ba0',
                }}
              >transparent</em> pricing.
            </h1>
            <p className="text-[clamp(0.95rem,2vw,1.12rem)] text-[#6b7280] max-w-[520px] mx-auto leading-relaxed">
              No per-model subscriptions. No surprise fees.
              <br className="hidden sm:block" />
              One plan covers image, video, and text.
            </p>

            {IS_ALPHA && (
              <Link
                to="/auth?tab=register"
                className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-full text-[0.78rem] font-medium no-underline transition-all hover:shadow-sm"
                style={{ background: 'rgba(200,212,90,0.13)', color: '#5c6b20' }}
              >
                7-day free trial — try every model
                <span className="opacity-50 ml-0.5">→</span>
              </Link>
            )}
          </div>
        </Container>
      </section>

      {/* ── SECTION 2: Plan Cards ── */}
      <section className="py-12 md:py-20 bg-white">
        <Container size="lg" className="max-w-[1060px]">
          <div ref={cardsRef} className="lp-reveal text-center mb-10 md:mb-12">
            <h2
              className="text-[clamp(1.8rem,4.5vw,2.75rem)] font-bold leading-[1.1] tracking-[-0.02em] mb-4"
              style={{ color: '#111827' }}
            >
              One plan. Every model.
            </h2>
            <p className="text-[clamp(0.95rem,2vw,1.08rem)] text-[#6b7280] max-w-[480px] mx-auto leading-relaxed">
              Monthly or yearly — same features, same models, same credits.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#c8d45a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="max-w-[820px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-start">

              {/* Monthly Card */}
              <div
                className="pricing-card-monthly rounded-2xl p-6 md:p-7 md:mt-8 transition-shadow duration-300"
                style={{ background: '#fafbfc', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
              >
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.12em] text-[#9ca3af] mb-4">Monthly</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-[2.6rem] font-bold tracking-tight text-[#1f2937] leading-none">
                    {mPrice ? fmtPrice(mPrice.unitAmount) : '$19'}
                  </span>
                  <span className="text-[0.85rem] text-[#9ca3af] font-medium">/mo</span>
                </div>
                <p className="text-[0.75rem] text-[#9ca3af] mb-5">Billed monthly. Cancel anytime.</p>

                {user ? (
                  <Link
                    to="/organization/credits"
                    className="w-full flex items-center justify-center py-[11px] rounded-xl text-[0.82rem] font-semibold no-underline transition-all duration-200 hover:border-[#9ca3af] hover:shadow-sm"
                    style={{ border: '1.5px solid #d1d5db', color: '#374151', background: '#fff' }}
                  >
                    Get Monthly
                  </Link>
                ) : (
                  <a
                    href={monthlyHref}
                    className="w-full flex items-center justify-center py-[11px] rounded-xl text-[0.82rem] font-semibold no-underline transition-all duration-200 hover:border-[#9ca3af] hover:shadow-sm"
                    style={{ border: '1.5px solid #d1d5db', color: '#374151', background: '#fff' }}
                  >
                    Get Monthly
                  </a>
                )}

                <div className="mt-5 pt-4 border-t border-[#f0f1f3]">
                  {getFeatures(monthly).filter((f) => f.isIncluded).map((f) => (
                    <FeatureRow
                      key={f.id ?? f.text}
                      text={f.text}
                      tooltipHtml={(f as any).tooltipHtml}
                      included
                      muted
                    />
                  ))}
                </div>
              </div>

              {/* Yearly Card (Hero) */}
              <div className="relative">
                <div
                  className="pricing-card-yearly rounded-2xl p-6 md:p-7 relative overflow-hidden transition-shadow duration-300"
                  style={{
                    background: 'linear-gradient(170deg, #151d2e 0%, #111827 30%, #0f1420 100%)',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 25px 60px rgba(0,0,0,0.18)',
                  }}
                >
                  <div className="absolute -top-16 -right-16 w-[180px] h-[180px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(200,212,90,0.08) 0%, transparent 70%)' }} />
                  <div className="absolute -bottom-24 -left-24 w-[200px] h-[200px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(200,212,90,0.04) 0%, transparent 70%)' }} />

                  <div className="relative z-[1]">
                    <div className="flex items-center gap-2.5 mb-4 mt-2">
                      <p className="text-[0.7rem] font-bold uppercase tracking-[0.1em] text-[#c8d45a]">Yearly</p>
                      {savings != null && (
                        <span className="text-[0.65rem] font-bold uppercase tracking-[0.04em] px-2.5 py-[3px] rounded-md" style={{ background: 'rgba(200,212,90,0.12)', color: '#c8d45a' }}>
                          Save ${savings}
                        </span>
                      )}
                    </div>

                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-[1.05rem] line-through font-medium" style={{ color: 'rgba(239,68,68,0.55)' }}>
                        {mPrice ? fmtPrice(mPrice.unitAmount) : '$19'}
                      </span>
                      <span className="text-[2.8rem] font-bold tracking-tight text-white leading-none">
                        {yMonthly != null ? fmtPrice(yMonthly) : '$15.83'}
                      </span>
                      <span className="text-[0.85rem] text-[#6b7280] font-medium">/mo</span>
                    </div>
                    <p className="text-[0.75rem] text-[#6b7280] mb-5">
                      Billed at {yPrice ? fmtPrice(yPrice.unitAmount) : '$190'}/year
                    </p>

                    {user ? (
                      <Link
                        to="/organization/credits"
                        className="w-full flex items-center justify-center py-3 rounded-xl text-[0.85rem] font-bold no-underline transition-all duration-200 hover:brightness-110 hover:shadow-lg"
                        style={{
                          background: 'linear-gradient(135deg, #d0dc5e 0%, #b8c442 100%)',
                          color: '#111807',
                          boxShadow: '0 2px 12px rgba(200,212,90,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                        }}
                      >
                        Get Annual →
                      </Link>
                    ) : (
                      <a
                        href={yearlyHref}
                        className="w-full flex items-center justify-center py-3 rounded-xl text-[0.85rem] font-bold no-underline transition-all duration-200 hover:brightness-110 hover:shadow-lg"
                        style={{
                          background: 'linear-gradient(135deg, #d0dc5e 0%, #b8c442 100%)',
                          color: '#111807',
                          boxShadow: '0 2px 12px rgba(200,212,90,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
                        }}
                      >
                        Get Annual →
                      </a>
                    )}

                    <div className="mt-5 pt-4 border-t border-white/[0.07]">
                      {getFeatures(yearly).map((f) => (
                        <FeatureRow
                          key={f.id ?? f.text}
                          text={f.text}
                          tooltipHtml={(f as any).tooltipHtml}
                          included={f.isIncluded}
                          dark
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trust + credit examples */}
          <div className="max-w-[460px] mx-auto mt-10 text-center">
            <p className="text-[0.68rem] text-[#9ca3af] flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" />
              Secure checkout via Stripe · Cancel anytime
            </p>
          </div>

        </Container>
      </section>

      {/* ── SECTION 3: Credit Calculator (Model Tables) ── */}
      <section className="py-12 md:py-20 bg-[#f5f7fa]">
        <Container size="lg" className="max-w-[1100px]">
          <div ref={calcRef} className="lp-reveal text-center mb-10">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#a8b44a] mb-3 font-semibold">
              Credit Calculator
            </p>
            <h2
              className="text-[clamp(1.6rem,3.5vw,2.2rem)] font-semibold leading-[1.15] tracking-tight mb-3"
              style={{ color: '#1d2939' }}
            >
              What 4,000 credits gets you
            </h2>
            <p className="text-[clamp(0.88rem,1.8vw,1rem)] text-[#6b7280] max-w-[500px] mx-auto leading-relaxed">
              Every plan includes all models. Here's how many generations you get each month.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ModelTable
              title="Image Generation"
              subtitle="30+ models available · default resolution · cheapest first"
              icon={<Image className="w-5 h-5 text-[#8b9656]" />}
              models={imageModels}
            />
            <ModelTable
              title="Video Generation"
              subtitle="20+ models available · default settings, no audio · cheapest first"
              icon={<Video className="w-5 h-5 text-[#8b9656]" />}
              models={videoModels}
            />
          </div>
        </Container>
      </section>

      {/* ── SECTION 4: How Credits Work ── */}
      <section className="py-12 md:py-20 bg-white">
        <Container size="lg" className="max-w-[1060px]">
          <div ref={creditsRef} className="lp-reveal">
            <div className="text-center mb-10">
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#a8b44a] mb-3 font-semibold">
                How It Works
              </p>
              <h2
                className="text-[clamp(1.6rem,3.5vw,2.2rem)] font-semibold leading-[1.15] tracking-tight"
                style={{ color: '#1d2939' }}
              >
                Credits made simple
              </h2>
            </div>

            <div className="max-w-[900px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card 1: How Credits Work */}
              <div className="bg-white rounded-2xl p-6 md:p-7 relative overflow-hidden" style={{ border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(200,212,90,0.4), transparent)' }} />
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(200,212,90,0.08)', border: '1px solid rgba(200,212,90,0.15)' }}>
                    <Zap className="w-5 h-5 text-[#8b9656]" />
                  </div>
                  <div>
                    <h3 className="text-[1rem] font-semibold text-[#1d2939]">How Credits Work</h3>
                    <p className="text-[0.72rem] text-[#a8b44a] font-medium mt-0.5">Simple, predictable AI pricing</p>
                  </div>
                </div>
                <div className="space-y-3 text-[0.85rem] text-[#5a6478] leading-relaxed">
                  <p>
                    Every plan gives you <strong className="text-[#1d2939]">4,000 credits per month</strong>. Each AI generation costs a set number of credits depending on the model — from just 1 credit for fast models to ~500 for premium video.
                  </p>
                  <div className="flex gap-3 py-3">
                    <div className="flex-1 rounded-lg p-3 text-center" style={{ background: '#f5f7fa', border: '1px solid #e5e7eb' }}>
                      <div className="text-lg font-bold text-[#1d2939]">~10</div>
                      <div className="text-[0.68rem] text-[#9ca3af] mt-0.5">credits / image</div>
                    </div>
                    <div className="flex-1 rounded-lg p-3 text-center" style={{ background: 'rgba(200,212,90,0.06)', border: '1px solid rgba(200,212,90,0.15)' }}>
                      <div className="text-lg font-bold text-[#8b9656]">4,000</div>
                      <div className="text-[0.68rem] text-[#a8b44a] mt-0.5">credits / month</div>
                    </div>
                    <div className="flex-1 rounded-lg p-3 text-center" style={{ background: '#f5f7fa', border: '1px solid #e5e7eb' }}>
                      <div className="text-lg font-bold text-[#1d2939]">400+</div>
                      <div className="text-[0.68rem] text-[#9ca3af] mt-0.5">images / month</div>
                    </div>
                  </div>
                  <p>
                    The exact credit cost is <strong className="text-[#1d2939]">shown before you confirm</strong> every generation — no surprises. Credits refresh at the start of each billing cycle.
                  </p>
                </div>
              </div>

              {/* Card 2: Credit Top-Ups */}
              <div className="bg-white rounded-2xl p-6 md:p-7 relative overflow-hidden" style={{ border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(74,222,128,0.4), transparent)' }} />
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.15)' }}>
                    <Coins className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className="text-[1rem] font-semibold text-[#1d2939]">Credit Top-Ups</h3>
                    <p className="text-[0.72rem] text-green-500 font-medium mt-0.5">Coming soon</p>
                  </div>
                </div>
                <div className="space-y-3 text-[0.85rem] text-[#5a6478] leading-relaxed">
                  <p>
                    Creative work is unpredictable. Some months you'll barely use your credits, other months you'll burn through them in a week. We get it.
                  </p>
                  <div className="rounded-lg p-4" style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#1d2939] font-medium">Top-Ups</span>
                      <span className="text-green-500 font-bold text-lg">Coming Soon</span>
                    </div>
                    <p className="text-[0.75rem] text-[#6b7280]">
                      We're building one-click credit top-ups so you never hit a wall mid-project.
                    </p>
                  </div>
                  <p>
                    For now, your 4,000 credits refresh automatically at the start of each billing cycle. Upgrading from Monthly to Yearly gives you the same credits at a better price.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── SECTION 5: FAQ ── */}
      <section className="py-12 md:py-20 bg-[#f5f7fa]" id="faq">
        <Container size="lg" className="max-w-[1100px]">
          <div ref={faqRef} className="lp-reveal text-center">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[#a8b44a] mb-3 font-semibold">
              FAQ
            </p>
            <h2
              className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight"
              style={{ color: '#1d2939' }}
            >
              Questions? Answered.
            </h2>
          </div>

          <div className="max-w-[620px] mx-auto mt-8">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b border-black/[0.06]">
                  <button
                    className="w-full flex items-center justify-between py-[1.1rem] text-left bg-transparent border-none cursor-pointer"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                  >
                    <span className="text-[0.9rem] font-medium text-[#1d2939]">{item.q}</span>
                    <ChevronDown
                      className="w-4 h-4 flex-shrink-0 ml-4 transition-transform duration-200 text-[#8c95a6]"
                      style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: isOpen ? 300 : 0, opacity: isOpen ? 1 : 0 }}
                  >
                    <p className="pb-[1.1rem] text-[0.82rem] text-[#5a6478] leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── SECTION 6: CTA ── */}
      <section className="py-12 md:py-20 text-center relative" style={{ background: '#1d2939' }}>
        <Container size="lg" className="max-w-[1100px]">
          <div ref={ctaRef} className="lp-reveal">
            <h2
              className="text-[clamp(1.8rem,4.5vw,2.8rem)] mb-3 leading-[1.1]"
              style={{
                fontFamily: "'Playfair Display',Georgia,serif",
                fontWeight: 400,
                fontStyle: 'italic',
                color: 'white',
              }}
            >
              {IS_ALPHA ? 'Start creating today.' : 'Start creating today.'}
            </h2>
            <p className="text-[0.95rem] mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {IS_ALPHA
                ? '7-day free trial. 60+ AI models. One visual canvas.'
                : '4,000 credits every month. 60+ AI models. One visual canvas.'}
            </p>
            <Link
              to={user ? '/canvas' : '/auth?tab=register'}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[0.9rem] font-medium text-[#1d2939] no-underline transition-all hover:scale-[1.03]"
              style={{
                background: 'linear-gradient(135deg, #c8d45a 0%, #a8b44a 100%)',
                boxShadow: '0 2px 12px rgba(200,212,90,0.3)',
              }}
            >
              {IS_ALPHA ? 'Start Creating' : 'Start Creating'}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-[0.7rem] mt-3" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {IS_ALPHA ? '7-day free trial · Cancel anytime' : 'Cancel anytime · No long-term commitment'}
            </p>
          </div>
        </Container>
      </section>

      <PublicFooter />

      {/* Scoped styles */}
      <style>{`
        @keyframes prcTipIn {
          from { opacity: 0; transform: translateY(3px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pricing-card-monthly:hover {
          box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
        }
        .pricing-card-yearly:hover {
          box-shadow: 0 4px 6px rgba(0,0,0,0.07), 0 30px 70px rgba(0,0,0,0.22) !important;
        }
        .pricing-model-card:hover {
          border-color: #d1d5db !important;
          box-shadow: 0 4px 16px rgba(0,0,0,0.06) !important;
        }
        .pricing-model-row:hover {
          background: #fafbfc;
        }
      `}</style>
    </div>
  );
}
